import {
    Box,
    Typography,
    Paper,
    Grid,
    Divider,
    TextField,
    Button,
    IconButton,
    MenuItem,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { useState, useEffect } from "react";
import * as dropdown from "../../services/dropdown_service";
import { formatDate } from "../../utils/formatDate";
import IMG from "../../images/istockphoto.jpg";

const BowMedicalHistoryDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { bowData: record, pet: petData, type } = location.state || {};

    const [vaccine_dropdown, setVaccineDropdown] = useState([]);
    const [parasite_dropdown, setParasiteDropdown] = useState([]);

    useEffect(() => {
        if (petData?.pet?.typeId) {
            fetchVaccine(petData.pet.typeId);
            fetchParasite(petData.pet.typeId);
        } else if (petData?.typeId) { // Fallback, just in case
            fetchVaccine(petData.typeId);
            fetchParasite(petData.typeId);
        }
    }, [petData]);

    const fetchVaccine = async (typeId) => {
        try {
            const res = await dropdown.getVaccine(typeId);
            setVaccineDropdown(res.data.results);
        } catch { }
    };
    const fetchParasite = async (typeId) => {
        try {
            const res = await dropdown.getParasite(typeId);
            setParasiteDropdown(res.data.results);
        } catch { }
    };

    if (!record || !petData) {
        return <Typography>ไม่พบข้อมูล กรุณาย้อนกลับ</Typography>;
    }

    // The actual pet object resides in petData (or petData.pet if it's the Link container)
    const pet = petData.pet || petData;

    // Safely extract fields based on typical REST payload
    const vetName = record.vet_name || record.handledBy || "-";
    const hospitalName = record.vet_hospital_name || record.hospital || "-";
    const licenseNo = record.vet_license_number || record.licenseNo || "-";
    const note = record.note || "-";
    const date = record.date || record.serviceDate || "";

    // The record might have an "items" array, or might be flat
    const items = record.items && record.items.length > 0 ? record.items : [record];

    return (
        <>
            <AppBreadcrumbs />

            <Box sx={{ p: 2 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    ประวัติการรับบริการส่วนกลาง {petData.hospitalPetCode || ""}
                </Typography>

                {/* ==============================
              ข้อมูลสัตว์เลี้ยง
          =============================== */}
                <Paper sx={{ p: 2, mb: 4 }}>
                    <Grid container spacing={2}>
                        {/* รูป */}
                        <Grid item xs={12} md={4}>
                            <img
                                src={pet.image ? pet.image : IMG}
                                alt={pet.name}
                                style={{ width: "70%", borderRadius: 8 }}
                            />
                        </Grid>

                        {/* ข้อมูล */}
                        <Grid item xs={12} md={8}>
                            {/* ชื่อสัตว์ */}
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                ชื่อ: {pet.name}
                            </Typography>

                            {/* ---------- ส่วนที่ 1: ข้อมูลสัตว์เลี้ยง ---------- */}
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", mt: 1 }}
                            >
                                ข้อมูลสัตว์เลี้ยง
                            </Typography>

                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        ชนิดสัตว์:
                                    </Typography>{" "}
                                    {pet.type}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        สายพันธุ์:
                                    </Typography>{" "}
                                    {pet.breed}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        วันเกิด:
                                    </Typography>{" "}
                                    {formatDate(pet.birthdate)}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        สีตา:
                                    </Typography>{" "}
                                    {pet.eyeColor}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        สีขน:
                                    </Typography>{" "}
                                    {pet.furColor}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        ตำหนิ:
                                    </Typography>{" "}
                                    {pet.appearanceDetail || "-"}
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        ไมโครชิป:
                                    </Typography>{" "}
                                    {pet.microchipNumber || "-"}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        กรุ๊ปเลือด:
                                    </Typography>{" "}
                                    {pet.bloodGroup}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        ยาที่แพ้:
                                    </Typography>{" "}
                                    {pet.drugAllergies || "-"}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        อาหารที่แพ้:
                                    </Typography>{" "}
                                    {pet.foodAllergies || "-"}
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography sx={{ fontWeight: "bold", display: "inline" }}>
                                        โรคประจำตัว:
                                    </Typography>{" "}
                                    {pet.diseases || "-"}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* ==============================
              รายการบริการ
          =============================== */}
                {items.map((it, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                            {type === "vaccine" ? "รายละเอียดวัคซีน" : type === "parasite" ? "รายละเอียดการรับยาป้องกันพยาธิ / ปรสิต" : "รายละเอียดไมโครชิพ"}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={type === "microchip" ? 6 : 6}>
                                <TextField
                                    label="วันที่ได้รับบริการ"
                                    fullWidth
                                    type="date"
                                    value={date ? new Date(date).toISOString().split("T")[0] : ""}
                                    disabled
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {type !== "microchip" && (
                                <Grid item xs={12}>
                                    <Typography sx={{ mb: 1 }}>รูปประกอบ</Typography>
                                    <Box
                                        sx={{
                                            border: "2px dashed #ccc",
                                            width: 200,
                                            height: 200,
                                            margin: "0 auto",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            cursor: "default",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {((it.photos && it.photos.length > 0 && it.photos[0].photo) || it.photo || it.image) ? (
                                            <img
                                                src={it.photos?.[0]?.photo || it.photo || it.image}
                                                alt="Record Image"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        ) : (
                                            <IconButton component="label">
                                                <PhotoCamera fontSize="large" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {it.product && type === "vaccine" && (
                                <Grid item xs={12} md={6}>
                                    <TextField select label="ชื่อวัคซีน / brand name vaccine" fullWidth value={String(it.product)} disabled>
                                        {!vaccine_dropdown.some(d => String(d.value) === String(it.product)) && (
                                            <MenuItem value={String(it.product)} disabled>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography>{it.product}</Typography>
                                                </Box>
                                            </MenuItem>
                                        )}
                                        {vaccine_dropdown.map((itemValue) => (
                                            <MenuItem key={itemValue.value} value={String(itemValue.value)} disabled>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <img
                                                        src={itemValue.context.photo}
                                                        alt={itemValue.label}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 4,
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                    <Typography>{itemValue.label}</Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            {it.product && type === "parasite" && (
                                <Grid item xs={12} md={6}>
                                    <TextField select label="ชื่อยาที่ได้รับ" fullWidth value={String(it.product)} disabled>
                                        {!parasite_dropdown.some(d => String(d.value) === String(it.product)) && (
                                            <MenuItem value={String(it.product)} disabled>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography>{it.product}</Typography>
                                                </Box>
                                            </MenuItem>
                                        )}
                                        {parasite_dropdown.map((itemValue) => (
                                            <MenuItem key={itemValue.value} value={String(itemValue.value)} disabled>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <img
                                                        src={itemValue.context.photo}
                                                        alt={itemValue.label}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 4,
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                    <Typography>{itemValue.label}</Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            {it.lot_number && (
                                <Grid item xs={12} md={6}>
                                    <TextField label="Lot No." fullWidth value={it.lot_number} disabled />
                                </Grid>
                            )}

                            {it.expiration_date && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="วันหมดอายุ / EXP date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        value={it.expiration_date ? new Date(it.expiration_date).toISOString().split("T")[0] : ""}
                                        disabled
                                    />
                                </Grid>
                            )}

                            {it.appointed_date && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="วันที่นัดครั้งถัดไป"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        value={it.appointed_date ? new Date(it.appointed_date).toISOString().split("T")[0] : ""}
                                        disabled
                                    />
                                </Grid>
                            )}

                            {it.microchip_number && type === "microchip" && (
                                <Grid item xs={12} md={6}>
                                    <TextField label="เลขไมโครชิพ" fullWidth value={it.microchip_number} disabled />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                ))}

                <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
                    สัตวแพทย์ / สถานพยาบาล
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField label="สัตวแพทย์ผู้ให้บริการ" fullWidth disabled value={vetName} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="เลขที่ใบอนุญาต" fullWidth disabled value={licenseNo} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="ชื่อสถานพยาบาล" fullWidth disabled value={hospitalName} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="ข้อมูลเพิ่มเติม" multiline minRows={3} fullWidth disabled value={note} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                    <Button variant="outlined" onClick={() => navigate(-1)}>ย้อนกลับ</Button>
                </Box>
                <Box sx={{ height: 50 }} />
            </Box>
        </>
    );
};

export default BowMedicalHistoryDetail;
